import React, { useEffect, useState, useCallback } from 'react';
import { Button, Input, Modal, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useApolloClient } from 'react-apollo';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import GET_NEXXEN_SEGMENT_SETS from '../../../core/GraphQl/Queries/GET_NEXXEN_SEGMENT_SETS';

const { Text } = Typography;

const CampaignRequestorNexxenSegmentsDialog = ({
  visible,
  setVisible,
  onAddSelections,
  selectedSegments = [],
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [segmentSets, setSegmentSets] = useState([]);
  const [loadingSegmentSets, setLoadingSegmentSets] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentSetsTotal, setSegmentSetsTotal] = useState(0);
  const [segmentSetsPage, setSegmentSetsPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { currentCampaignOrder } = useCampaignRequestorContext();
  const client = useApolloClient();

  const nexxenAdvertiserId = currentCampaignOrder?.advertiser?.dataConfig?.dspAdvertiserId;

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleAddSelections = () => {
    const selectedItems = segmentSets
      .filter((item) => selectedRowKeys.includes(item.id))
      .map((item) => ({
        segmentId: item.id,
        name: item.name,
        segmentType: 'segmentSet',
        cost: item.cpmCost || item.cost || 0,
      }));

    onAddSelections(selectedItems);
    setVisible(false);
  };

  const rowSelection = {
    type: 'radio',
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const fetchSegmentSets = useCallback(async (page = 1, size = pageSize) => {
    if (!nexxenAdvertiserId) {
      setSegmentSets([]);
      setSegmentSetsTotal(0);
      return;
    }

    setLoadingSegmentSets(true);
    try {
      const offset = (page - 1) * size;
      const { data } = await client.query({
        query: GET_NEXXEN_SEGMENT_SETS,
        variables: {
          advertiserId: nexxenAdvertiserId,
          isReusable: true,
          offset,
          limit: size,
        },
        fetchPolicy: 'network-only',
      });

      setSegmentSets(data?.nexxenSegmentSets?.data || []);
      setSegmentSetsTotal(data?.nexxenSegmentSets?.queryTotal || 0);
    } catch (error) {
      console.error('Error fetching segment sets:', error);
      message.error('Failed to load segment sets');
      setSegmentSets([]);
      setSegmentSetsTotal(0);
    } finally {
      setLoadingSegmentSets(false);
    }
  }, [client, nexxenAdvertiserId, pageSize]);

  useEffect(() => {
    if (visible) {
      fetchSegmentSets(segmentSetsPage, pageSize);
    }
  }, [visible, segmentSetsPage, pageSize, fetchSegmentSets]);

  useEffect(() => {
    if (selectedSegments && selectedSegments.length > 0) {
      const keys = selectedSegments.map((s) => s.segmentId);
      setSelectedRowKeys(keys);
    } else {
      setSelectedRowKeys([]);
    }
  }, [selectedSegments, visible]);

  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter((item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const segmentSetsColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      ellipsis: true,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: 'CPM Cost',
      dataIndex: 'cpmCost',
      key: 'cpmCost',
      width: 100,
      render: (value) => (value ? `$${value.toFixed(2)}` : '-'),
    },
    {
      title: 'Reusable',
      dataIndex: 'isReusable',
      key: 'isReusable',
      width: 100,
      render: (value) => (
        <Tag color={value ? 'blue' : 'default'}>
          {value ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status || 'Unknown'}
        </Tag>
      ),
    },
  ];

  const handleSegmentSetsPaginationChange = (page, size) => {
    setSegmentSetsPage(page);
    if (size !== pageSize) {
      setPageSize(size);
    }
  };

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 20,
          }}
        >
          <span>NEXXEN SEGMENT SETS</span>
          <div>
            <Button
              type="primary"
              disabled={selectedRowKeys.length === 0}
              onClick={() => handleAddSelections()}
            >
              Add Selection
            </Button>
          </div>
        </div>
      }
      visible={visible}
      onCancel={() => setVisible(false)}
      footer={null}
      width={900}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Text type="secondary">
          Select a segment set to add to this line item for audience targeting.
          Only one segment set can be attached per line item.
        </Text>

        <Input
          placeholder="Search segment sets by name or description..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />

        {!nexxenAdvertiserId ? (
          <Space direction="vertical" style={{ width: '100%', padding: 40 }} align="center">
            <Text type="secondary">
              No Nexxen Advertiser ID found. Segment sets require an advertiser connection.
            </Text>
          </Space>
        ) : loadingSegmentSets ? (
          <Space direction="vertical" style={{ width: '100%', padding: 40 }} align="center">
            <Spin />
            <Text type="secondary">Loading segment sets...</Text>
          </Space>
        ) : (
          <Table
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
            columns={segmentSetsColumns}
            dataSource={filterData(segmentSets)}
            pagination={{
              current: segmentSetsPage,
              pageSize: pageSize,
              total: segmentSetsTotal,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: handleSegmentSetsPaginationChange,
            }}
            scroll={{ y: 400 }}
            size="small"
          />
        )}
      </Space>
    </Modal>
  );
};

export default CampaignRequestorNexxenSegmentsDialog;
